import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import AppFooter from '../components/AppFooter';
import PublicHeader from '../components/PublicHeader';

/**
 * Public-facing page aimed at organisations who have just received a Subject
 * Access Request via Precious Data. Linked from every SAR email footer.
 *
 * Tone: warm-but-firm. The page must establish that the request is legally
 * identical to one written by hand; refusing to comply because it came via
 * a third-party tool is itself unlawful and reportable to the ICO.
 */
const ForOrganisations = () => {
  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="for-orgs-page">
      <PublicHeader />

      {/* Hero */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
            For organisations responding to a Subject Access Request
          </span>
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            What is Precious Data?
          </h1>
          <p className="text-[19px] leading-[1.5] text-ink-70">
            Precious Data is a free, secure tool that helps care leavers in the UK
            understand their own life history. If you have just received a Subject
            Access Request that mentions us, this page explains who we are, why
            you&apos;ve received it, and how to respond.
          </p>
        </div>
      </section>

      {/* Top-line cards */}
      <section className="px-7 lg:px-12 py-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-3">
          <div className="bg-ground rounded-lg p-6 space-y-2">
            <p className="font-heading text-[19px] font-semibold tracking-[-0.02em]">A legal request</p>
            <p className="text-[15px] text-ink-70 leading-relaxed">
              The request is from the data subject themselves. It is made under
              Article 15 UK GDPR and the Data Protection Act 2018. You must
              respond within one calendar month.
            </p>
          </div>
          <div className="bg-ground rounded-lg p-6 space-y-2">
            <p className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Sent by them, not us</p>
            <p className="text-[15px] text-ink-70 leading-relaxed">
              We provide the platform. The user wrote, signed and sent this
              request themselves. Treat it the way you would treat one written
              on paper, because legally it is identical.
            </p>
          </div>
          <div className="bg-ground rounded-lg p-6 space-y-2">
            <p className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Their data is safe</p>
            <p className="text-[15px] text-ink-70 leading-relaxed">
              Anything you send is encrypted on our servers with keys only the
              user holds. We cannot read it. Neither can anyone else.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Why you received this */}
      <Section eyebrow="Why you received a request through us" title="A care leaver is asking for their records.">
        <p>
          Care leavers — adults who spent part of their childhood in the UK
          care system — are entitled to a copy of every record an
          organisation holds about them. For most, this is the only way to
          piece together a coherent story of their own childhood: who they
          lived with, why decisions were made, what professionals thought of
          them, what was missed.
        </p>
        <p>
          Precious Data exists because doing this alone is overwhelming. We
          help the user identify which organisations to contact, draft a
          compliant request, send it from their own email account or via our
          relay, and securely receive your response.
        </p>
        <p>
          <strong className="text-ink">The user is the data subject.</strong>{' '}
          They authored the request. They will read your response. We are
          only the tool that helps them organise the process, in the same
          way a solicitor or advocate would.
        </p>
      </Section>

      {/* Section: This request is valid + must be complied with */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-amber-wash px-7 py-12 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-5 text-amber-ink">
          <p className="pd-eyebrow text-amber-ink">Your legal obligation</p>
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
            You must comply. The medium does not matter.
          </h2>
          <div className="space-y-4 text-ink-70 leading-relaxed">
            <p>
              UK GDPR is explicit:{' '}
              <strong className="text-ink">
                a Subject Access Request is valid in any form
              </strong>{' '}
              — letter, email, web form, or platform like ours. You may not
              refuse, delay or impose extra conditions because the request
              arrived through a third-party tool.
            </p>
            <p>The Information Commissioner&apos;s Office is clear that:</p>
            <ul className="space-y-2 list-disc pl-5">
              <li>
                You must respond <strong className="text-ink">within one
                calendar month</strong> of receiving the request (Article 12(3)).
              </li>
              <li>
                You may verify the requester&apos;s identity, but only with{' '}
                <strong className="text-ink">reasonable</strong> proof,
                and you must explain exactly what you need. Asking for
                excessive ID is a tactic the ICO warns against.
              </li>
              <li>
                Redactions must be{' '}
                <strong className="text-ink">individually justified</strong>{' '}
                against a specific exemption. Blanket redaction is not lawful.
              </li>
              <li>
                Refusing or ignoring a request because of the route it came in
                through is itself a breach. The data subject can, and our
                users will, escalate to the ICO.
              </li>
            </ul>
            <p className="rounded-lg bg-surface p-5 text-ink">
              <strong>If you have any doubt about authenticity</strong>, reply
              directly to the email address the request came from. The
              request was sent by the data subject themselves, so replies go
              straight back to them, never to us.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Authorised third parties */}
      <Section eyebrow="Authorised third parties" title="What if a solicitor or advocate is involved?">
        <p>
          Some users authorise a trusted third party — a solicitor, legal
          advocate, support worker, counsellor, therapist, GP or family
          member — to help them work through their records. If you receive
          correspondence from someone other than the data subject, here&apos;s
          what to expect and how to handle it.
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Every authorised party is granted access through Precious Data
            using a cryptographically signed authorisation. We can produce
            a <strong className="text-ink">written authorisation
            letter</strong> on request, signed by the data subject and
            carrying a unique reference number.
          </li>
          <li>
            Each letter has a{' '}
            <strong className="text-ink">QR code and reference
            number</strong>. You can verify it at{' '}
            <Link to="/verify" className="text-brand underline hover:opacity-90">
              preciousdata.org.uk/verify
            </Link>
            . The page confirms the data subject, the third party, the
            scope of authority and the date it was issued, and shows
            whether it has been revoked.
          </li>
          <li>
            <strong className="text-ink">
              Reply to the email the request arrived from, never out-of-band.
            </strong>{' '}
            That address routes back to the data subject (and any third
            parties they have authorised). Sending the response to a
            solicitor&apos;s personal address, or any address that wasn&apos;t on
            the original request, risks unlawful disclosure.
          </li>
          <li>
            If a third party contacts you{' '}
            <strong className="text-ink">without going through
            the platform</strong> — for example a phone call or an email from a
            different domain — pause. Ask them to verify their authority
            via the link above, or contact the data subject directly to
            confirm.
          </li>
        </ul>
      </Section>

      {/* Section: How to respond */}
      <Section eyebrow="How to respond" title="Reply by email. That is the law." tinted>
        <p>
          UK GDPR Article 12(3) is explicit:{' '}
          <strong className="text-ink">
            if the request was made electronically, the response must also
            be provided by electronic means
          </strong>{' '}
          unless the data subject specifically asks otherwise. Posting paper
          copies in response to an emailed request is not lawful compliance.
        </p>
        <p>
          <strong className="text-ink">In practice:</strong> press
          &quot;Reply&quot; on the original email and attach the records. Each reply
          goes directly to the data subject; we do not intercept it.
        </p>

        <div className="grid md:grid-cols-2 gap-3 pt-2">
          <div className="bg-surface rounded-lg p-5 space-y-2">
            <p className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">Email reply</p>
            <p className="text-[15px] text-ink-70 leading-relaxed">
              Reply to the original request and attach the records as PDFs
              (or a single ZIP). For very large bundles, encrypt with a
              password and share the password in a separate email.
            </p>
          </div>
          <div className="bg-surface rounded-lg p-5 space-y-2">
            <p className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">Secure upload link</p>
            <p className="text-[15px] text-ink-70 leading-relaxed">
              Many of our users include a one-time secure upload link in
              their request. Files uploaded through it are encrypted with the
              requester&apos;s key the moment they arrive; only the
              requester can ever read them.
            </p>
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <p>
            <strong className="text-ink">What to include:</strong> all
            personal data you hold about the requester — case notes, LAC
            reviews, care plans, correspondence, internal emails about them,
            meeting minutes, photos, electronic and paper records (scanned).
            The ICO has{' '}
            <a
              href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/"
              target="_blank"
              rel="noreferrer"
              className="text-brand underline hover:opacity-90"
            >
              detailed guidance
            </a>{' '}
            on what counts.
          </p>
          <p>
            <strong className="text-ink">If you need an extension:</strong>{' '}
            you may extend by up to two further months for genuinely complex
            requests, but you{' '}
            <strong className="text-ink">
              must inform the requester within the first month
            </strong>{' '}
            and explain why, by email.
          </p>
        </div>
      </Section>

      {/* Section: How we keep their data safe */}
      <Section eyebrow="Data security" title="We can't read what you send.">
        <p>
          Precious Data uses zero-knowledge encryption. Every file an
          organisation sends is encrypted with keys derived
          from the user&apos;s password. Those keys live only on their device and
          are discarded the moment they sign out.
        </p>
        <p>
          That means even if our servers were compromised, the files would
          remain unreadable. Our staff cannot access them, our hosting
          provider cannot access them, and we cannot share them with anyone
          — not insurers, not employers, not government bodies.
        </p>
        <p>
          You can read the full plain-English breakdown on our{' '}
          <Link to="/privacy" className="text-brand underline hover:opacity-90">
            privacy page
          </Link>
          .
        </p>
      </Section>

      {/* FAQ */}
      <Section eyebrow="Common questions" title="Questions we are often asked" tinted>
        <div className="space-y-2" data-testid="for-orgs-faq">
          <FaqItem
            q="Can I refuse because the request came via a third-party tool?"
            a="No. UK GDPR makes no distinction between requests written by hand, sent by email, or made through a tool like ours. Article 12 requires you to respond. Refusing on the basis of medium is itself a breach and reportable to the ICO."
          />
          <FaqItem
            q="Can we post the records back instead of emailing?"
            a="No, not unless the data subject has specifically asked for post. Article 12(3) requires the response to be provided by the same means the request was made when that was electronic. The user emailed you; you must email them back. Posting paper bundles in response to an emailed request is not lawful compliance and slows the user down significantly."
          />
          <FaqItem
            q="Can I require ID before responding?"
            a={`Only if you have a reasonable doubt about identity, and only what's necessary to resolve that doubt. The ICO warns against using ID requests as a delay tactic. If the user has provided their name, date of birth, addresses they lived at while in your care and reference numbers, that is normally sufficient for care records.`}
          />
          <FaqItem
            q="A solicitor or advocate is corresponding on the user's behalf. What should I do?"
            a="Ask for the written authorisation letter (we generate them with a QR code and reference number). Verify it at preciousdata.org.uk/verify. Reply to the email the request arrived from, not out-of-band; that address routes to both the data subject and the authorised third party. Never send the response to a personal email address that wasn't on the original request."
          />
          <FaqItem
            q="The user mentioned previous names. Should I search under those?"
            a="Yes. Many care leavers were known by different names during placements (foster carers' surnames, for example). You must search every name they tell you. Failing to do so risks omitting their personal data."
          />
          <FaqItem
            q="What about exemptions?"
            a="Apply them sparingly and individually. Schedule 2, paragraph 16 (third-party data), Schedule 2, paragraph 19 (legal professional privilege) and Schedule 3 Part 2 (social work data — health, social care, education) each have specific tests. Document your reasoning for each redaction. Disproportionate redaction is a common ICO complaint."
          />
          <FaqItem
            q="What if we no longer hold records about the requester?"
            a="Reply within the deadline saying so, with the date records were destroyed and the retention policy that authorised it. Care records have very long statutory retention, typically until the data subject is 75. If yours have gone earlier, expect questions."
          />
          <FaqItem
            q="What if the request is unclear?"
            a="You may ask for clarification, which can pause the response clock under Article 12A (Data (Use and Access) Act 2025). Be specific about what you need. You cannot use this to delay indefinitely."
          />
          <FaqItem
            q="Can we charge a fee?"
            a="Only for manifestly unfounded or excessive requests, or for additional copies. A standard request from a care leaver is neither. Charging would itself be a breach."
          />
          <FaqItem
            q="Who do we contact if we have a question?"
            a="Reply directly to the user; they own the request and the response. Procedural questions about Precious Data itself can be sent to hello@preciousdata.org.uk."
          />
        </div>
      </Section>

      {/* CTA */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-3xl mx-auto space-y-5">
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
            Help a care leaver understand their own life.
          </h2>
          <p className="text-ink-70 leading-relaxed">
            A timely, complete, properly reasoned response is the single most
            useful thing your organisation can do for the people in this
            community. Thank you for taking it seriously.
          </p>
          <div className="pt-2">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = 'mailto:hello@preciousdata.org.uk'}
              data-testid="cta-contact-btn"
            >
              hello@preciousdata.org.uk
            </Button>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

/** A prose section: eyebrow, page-level heading, body. `tinted` puts it on a ground panel. */
const Section = ({ eyebrow, title, tinted = false, children }) => (
  <section className={tinted ? 'mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-12 lg:px-12' : 'px-7 lg:px-12 py-12'}>
    <div className="max-w-3xl mx-auto space-y-5">
      <p className="pd-eyebrow">{eyebrow}</p>
      <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
        {title}
      </h2>
      <div className="space-y-4 text-ink-70 leading-relaxed">{children}</div>
    </div>
  </section>
);

const FaqItem = ({ q, a }) => (
  <details className="group rounded-lg bg-surface px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
    <summary className="cursor-pointer flex items-start justify-between gap-3 text-ink font-semibold list-none min-h-[28px]">
      <span>{q}</span>
      <span className="text-ink-55 group-open:rotate-45 transition-transform duration-state text-xl leading-none flex-shrink-0" aria-hidden="true">
        +
      </span>
    </summary>
    <p className="mt-3 text-[15px] text-ink-70 leading-relaxed">{a}</p>
  </details>
);

export default ForOrganisations;
